import type { SecurityAttributeSetting, InvApplication } from '@invision/core';
import { ApplicationHelpers } from '@invision/core';
import {
  getModuleAccessRequirement,
  getAccessibleApplications
} from '../constants/moduleSecurityAttributes.constants';
import { AuthLogger } from '../utils/logger';

/**
 * The result of validating a post-login redirect against the user's module access.
 *
 * - `allowed`  — the user may go to `redirectPath` as-is.
 * - `fallback` — the user cannot access the requested module but can access
 *                others; `redirectPath` points at `targetApplication` and
 *                `deniedApplication` describes what they asked for.
 * - `no-access` — the user has access to no modules at all; there is no valid
 *                destination and the caller should show the no-access page.
 */
export type RedirectOutcome = 'allowed' | 'fallback' | 'no-access';

export interface ModuleAccessValidationResult {
  /** Classification of the redirect decision, used by the caller to route. */
  outcome: RedirectOutcome;
  /** The validated destination path (empty for `no-access`). */
  redirectPath: string;
  /** The module the user requested but cannot access (set for `fallback`). */
  deniedApplication?: InvApplication;
  /** The module the user is being sent to (set for `allowed`/`fallback`). */
  targetApplication?: InvApplication;
  /** Whether the user has access to the requested module. */
  hasAccess: boolean;
  /** Whether the destination differs from what was requested. */
  isFallback: boolean;
}

/**
 * Validate a requested post-login redirect against the modules the user can
 * access and decide where they should actually land.
 *
 * Authentication-internal and unrecognized (public) paths are always allowed.
 * For module paths, the user is sent to the requested module when permitted;
 * otherwise they are redirected to their first accessible module, or reported
 * as having no accessible modules at all.
 */
export function validateAndSecureRedirect(
  requestedPath: string,
  userSecurityAttributes: SecurityAttributeSetting[]
): ModuleAccessValidationResult {
  // Authentication-internal paths never require module access.
  if (requestedPath.startsWith('/authentication')) {
    return {
      outcome: 'allowed',
      redirectPath: requestedPath,
      hasAccess: true,
      isFallback: false
    };
  }

  // Paths that map to no known module are treated as public - allow them.
  const moduleRequirement = getModuleAccessRequirement(requestedPath);
  if (!moduleRequirement) {
    return {
      outcome: 'allowed',
      redirectPath: requestedPath,
      hasAccess: true,
      isFallback: false
    };
  }

  const accessibleApps = getAccessibleApplications(userSecurityAttributes);

  const hasAccess = accessibleApps.some(
    (app) => app === moduleRequirement.application
  );

  if (hasAccess) {
    return {
      outcome: 'allowed',
      redirectPath: requestedPath,
      targetApplication: moduleRequirement.application,
      hasAccess: true,
      isFallback: false
    };
  }

  // The user cannot access the requested module and has no others - they are
  // stranded and must be shown the dedicated no-access page.
  if (accessibleApps.length === 0) {
    AuthLogger.error('No accessible modules found for user', {
      requestedPath,
      userSecurityAttributes: userSecurityAttributes.map((attr) => ({
        id: attr.Attribute,
        access: attr.Access
      }))
    });

    return {
      outcome: 'no-access',
      redirectPath: '',
      deniedApplication: moduleRequirement.application,
      hasAccess: false,
      isFallback: true
    };
  }

  // The requested module is off-limits, but the user can go to another module.
  const targetApplication = accessibleApps[0];
  return {
    outcome: 'fallback',
    redirectPath: ApplicationHelpers.getApplicationEntryPath(targetApplication),
    deniedApplication: moduleRequirement.application,
    targetApplication,
    hasAccess: false,
    isFallback: true
  };
}
